
import { ListOrgUsers, AddUserToOrg, RemoveUserFromOrg, AssignRolesToUser } from '@/api/orgAdmin'
import { ListDepts, AddDeptMember } from '@/api/orgAdmin'
import { ListRoles } from '@/api/orgAdmin'
import { RecordStatus, IAllUserInfo, IAllDeptInfo, IAllRoleInfo} from '@/api/types'
import Pagination from '@/components/Pagination/index.vue'
import { formatDate } from '@/utils/index'
import { Form as ElForm } from 'element-ui'
import { Component, Vue } from 'vue-property-decorator'


@Component({
  name: 'OrgAdminUsers',
  components: {
    Pagination
  }
})
export default class extends Vue {
  public userList: IAllUserInfo[] = []
  public total = 0
  public listLoading = true
  public deptList: IAllDeptInfo[] = []
  public deptLoading = false
  public roleList: IAllRoleInfo[] = []
  public roleLoading = false
  public inputKeyword = ''

  public userQuery = {
    pageNum: 1,
    pageSize: 50,
    keywords: [] as string[],
    status: undefined,
    deptIds: [] as number[],
    roleIds: [] as number[],
    notInDeptIds: [] as number[]
  }

  public statusOptions = [
    { label: '全部', value: undefined },
    { label: '启用', value: [RecordStatus.Enabled] },
    { label: '禁用', value: [RecordStatus.Disabled] }
  ]

  // 添加用户相关
  public createDialogVisible = false
  public createForm = {
    username: '',
    phone: ''
  }

  public createRules = {
    username: [
      { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
    ],
    phone: [
      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
    ]
  }

  // 角色相关
  public roleDialogVisible = false
  public roleForm = {
    userId: 0,
    roleIds: [] as number[]
  }

  public roleRules = {
    roleIds: [
      { required: true, message: '请选择角色', trigger: 'change', type: 'array' }
    ]
  }

  // 部门相关
  public deptDialogVisible = false
  public deptForm = {
    userId: 0,
    deptId: null as number | null,
    isLeader: false
  }

  public deptRules = {
    deptId: [
      { required: true, message: '请选择部门', trigger: 'change' }
    ]
  }

  created() {
    this.getUserList()
    this.fetchDepts()
    this.fetchRoles()
  }

  public async getUserList() {
    this.listLoading = true
    try {
      const response = await ListOrgUsers(this.userQuery)
      if (response && response.data) {
        const responseData = response.data as { items: IAllUserInfo[], total: number }
        this.userList = Array.isArray(responseData.items) ? responseData.items : []
        this.total = responseData.total || 0

        // 调试输出
        if (this.userList.length > 0) {
          // console.log(this.userList)
        }
      } else {
        this.userList = []
        this.total = 0
      }
    } catch (error) {
      console.error('获取用户列表失败:', error)
      this.$message.error('获取用户列表失败')
      this.userList = []
      this.total = 0
    } finally {
      this.listLoading = false
    }
  }

  public async fetchDepts() {
    try {
      this.deptLoading = true
      const response = await ListDepts()
      if (response && response.data) {
        this.deptList = Array.isArray(response.data) ? response.data : []
      } else {
        this.deptList = []
      }
    } catch (error) {
      console.error('获取部门列表失败:', error)
      this.deptList = []
    } finally {
      this.deptLoading = false
    }
  }

  public async fetchRoles() {
    try {
      this.roleLoading = true
      const response = await ListRoles()
      if (response && response.data) {
        this.roleList = Array.isArray(response.data) ? response.data : []
        // 调试输出
        // console.log('角色列表数据结构:', JSON.stringify(this.roleList.slice(0, 2)))
      } else {
        this.roleList = []
      }
    } catch (error) {
      console.error('获取角色列表失败:', error)
      this.roleList = []
    } finally {
      this.roleLoading = false
    }
  }

  public handleFilter() {
    this.userQuery.pageNum = 1
    this.getUserList()
  }

  public formatTime(time: string) {
    if (!time) return ''
    return formatDate(time, 'YYYY-MM-DD HH:mm:ss')
  }

  public handleCreate() {
    this.createForm = {
      username: '',
      phone: ''
    }
    this.createDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.createForm as ElForm).clearValidate()
    })
  }

  public async addUserToOrg() {
    // 验证至少有一个字段不为空
    if (!this.createForm.username && !this.createForm.phone) {
      this.$message.error('用户名和手机号至少填写一项')
      return
    }

    try {
      await AddUserToOrg({
        username: this.createForm.username || undefined,
        phone: this.createForm.phone || undefined
      })
      this.$message.success('用户添加成功')
      this.createDialogVisible = false
      this.getUserList()
    } catch (error) {
      console.error('添加用户失败:', error)
    }
  }

  public handleRemoveFromOrg(row: IAllUserInfo) {
    if (!row || !row.user || !row.user.id) {
      this.$message.error('用户数据不完整，无法执行此操作')
      return
    }

    this.$confirm('确定要将该用户从租户中移出吗?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      try {
        await RemoveUserFromOrg({ userId: row.user.id })
        this.$message.success('用户已从租户移出')
        this.getUserList()
      } catch (error) {
        console.error('移出用户失败:', error)
        this.$message.error('移出用户失败')
      }
    }).catch(() => {
      // 用户取消操作，不做任何处理
    })
  }

  public handleAssignRoles(row: IAllUserInfo) {
    if (!row || !row.user || !row.user.id) {
      this.$message.error('用户数据不完整，无法执行此操作')
      return
    }

    this.roleForm.userId = row.user.id
    // 提取角色ID
    if (row.roles && Array.isArray(row.roles)) {
      this.roleForm.roleIds = row.roles.map(r => r.role.id)
    } else {
      this.roleForm.roleIds = []
    }

    this.roleDialogVisible = true
  }

  public async assignRoles() {
    try {
      await AssignRolesToUser({
        userId: this.roleForm.userId,
        roleIds: this.roleForm.roleIds
      })
      this.$message.success('角色分配成功')
      this.roleDialogVisible = false
      this.getUserList()
    } catch (error) {
      console.error('角色分配失败:', error)
    }
  }

  public handleAddToDept(row: IAllUserInfo) {
    if (!row || !row.user || !row.user.id) {
      this.$message.error('用户数据不完整，无法执行此操作')
      return
    }

    this.deptForm.userId = row.user.id
    this.deptForm.deptId = null
    this.deptForm.isLeader = false
    this.deptDialogVisible = true
  }

  public async addToDept() {
    try {
      if (this.deptForm.deptId !== null) {
        await AddDeptMember({
          userId: this.deptForm.userId,
          deptId: Number(this.deptForm.deptId),
          isLeader: this.deptForm.isLeader
        })
        this.$message.success('添加至部门成功')
        this.deptDialogVisible = false
        this.getUserList()
      }
    } catch (error) {
      console.error('添加至部门失败:', error)
    }
  }

  // 辅助方法，获取用户ID
  public getUserId(row: IAllUserInfo): number | null {
    if (row && row.user && row.user.id) {
      return row.user.id
    }
    return null
  }

  // 辅助方法，获取用户名
  public getUsername(row: IAllUserInfo): string {
    if (row && row.user && row.user.username) {
      return row.user.username
    }
    return '-'
  }

  // 辅助方法，获取手机号
  public getPhone(row: IAllUserInfo): string {
    if (row && row.user && row.user.phone) {
      return row.user.phone
    }
    return '-'
  }

  // 辅助方法，处理角色数据
  public getRoles(row: IAllUserInfo): any[] {
    if (row && row.roles && Array.isArray(row.roles) && row.roles.length > 0) {
      return row.roles.filter(r => r && r.role)
    }
    return []
  }

  public hasRoles(row: IAllUserInfo): boolean {
    return Boolean(row && row.roles && Array.isArray(row.roles) && row.roles.length > 0)
  }

  // 辅助方法，获取部门数据
  public getDepts(row: IAllUserInfo): any[] {
    if (row && row.depts && Array.isArray(row.depts) && row.depts.length > 0) {
      return row.depts.filter((d: any) => d && d.dept)
    }
    return []
  }

  public hasDepts(row: IAllUserInfo): boolean {
    return Boolean(row && row.depts && Array.isArray(row.depts) && row.depts.length > 0)
  }

  // 计算属性：有效的部门列表
  get validDeptList() {
    // return this.deptList.filter(item => item && item.dept)
    return this.deptList
  }

  // 计算属性：有效的角色列表
  get validRoleList() {
    // console.log(this.roleList)
    return this.roleList
  }

  public handleAddKeyword() {
    const keyword = this.inputKeyword.trim()
    if (keyword && !this.userQuery.keywords.includes(keyword)) {
      this.userQuery.keywords.push(keyword)
      this.inputKeyword = ''
    }
  }

  public handleRemoveKeyword(index: number) {
    this.userQuery.keywords.splice(index, 1)
  }
}
